import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { getCookie } from 'widgets/toolbox/cookie';

/**
 * @description Base HarmonyProductDetail implementation
 * @param {ProductDetail} ProductDetail widget for extending
 * @returns {typeof HarmonyProductDetail} HarmonyProductDetail class
 */
export default function (ProductDetail) {
    /**
     * @class HarmonyProductDetail
     * @augments ProductDetail
     */
    class HarmonyProductDetail extends ProductDetail {
        init() {
            super.init();
        }

        /**
         * @description Scroll to bundled product section on PDP
         * @returns {void}
         */

        scrollToBundledItems() {
            var bundledProductsSection = this.ref('bundledProductsSection').get(0);
            bundledProductsSection.focus();
            scrollWindowTo(bundledProductsSection);
        }

        /**
         * @description Update Product View
         * @param {any} product productModel
         * @returns {void}
         */
        updateProductView(product) {
            super.updateProductView(product);

            if (Number(this.prefs().selectedQuantity) === product.selectedQuantity) {
                this.eventBus().emit('monetate.variation.select', product);
            }

            this.setPref('selectedQuantity', product.selectedQuantity);
            this.setPref('currentProductID', product.id);
            this.setPref('readyToOrder', product.readyToOrder);

            this.renderRatingBadgeLong(product);
            this.renderBrandLabel(product);
            this.renderShipToErrorMsg(product);
            this.updateAddToCartButton(product);
            this.renderAddToCartButton(product);
            this.handleBackInStockNotification(product);
        }

        // /**
        //  * @description Update social links. The function was overloaded. It is empty, because the social links aren't used.
        //  * @param {object} product Product object
        //  * @returns {void}
        //  */
        // updateSocialLinks() { }

        /**
         * @description Render Quantities
         * @param {object} product
         */
        renderQuantities(product) {
            const inputQuanity = this.getById('input-quantity', (quantity) => quantity);

            if (inputQuanity && product.quantities) {
                var isQtyDisabled = ((product.availability && product.availability.isOutOfStock) || product.isEmptyPrice);

                inputQuanity.render('template', {
                    value: product.selectedQuantity,
                    minOrderQuantity: product.minOrderQuantity,
                    maxOrderQuantity: product.maxOrderQuantity,
                    qtyStep: product.qtyStep,
                    url: product.variationUrl,
                    qtyUrl: product.quantityUrl,
                    isQtyDisabled: isQtyDisabled,
                    shipToRestrictedStates: product.shipToRestrictedStates,
                    productId: product.id
                }).then(() => this.eventBus().emit('shipto.restrictions.updated.quantity'));

                // trigger event if 'isQtyDisabled' equals 'true'
                if (isQtyDisabled) {
                    this.eventBus().emit('product.qty.disabled', isQtyDisabled);
                }
            } else {
                super.renderQuantities(product);
            }
        }

        /**
         * @description Render Rating Badge Long
         * @param {object} product
         */
        renderRatingBadgeLong(product) {
            this.getById('ratingBadgeLong', (ratingBadgeLong) => {
                ratingBadgeLong.render('template', { ratingBadgeLong: product.ratingBadgeLong });
            });
        }

        /**
         * @description Render product price
         * @param {object} product Product object
         * @returns {void}
         */
        renderPrice(product) {
            if (product.price && product.price.html) {
                this.eachChild(widget => {
                    if (widget.config && widget.config.id === 'priceBlock') {
                        widget.render('template', [], undefined, product.price.html);
                    }
                });
            }
        }

        /**
         * @description Render Brand Label
         * @param {object} product
         */
        renderBrandLabel(product) {
            this.getById('brandLabel', (brandLabel) => {
                brandLabel.render('template', {
                    brand: product.brand,
                    brandLabel: product.brandLabel
                });
            });
        }

        /**
         * @description Render Ship To Error Msg
         * @param {object} product
         */
        renderShipToErrorMsg(product) {
            this.getById('shiptoerror', (shiptoerror) => {
                shiptoerror.render('template', { shipToRestrictedStates: JSON.stringify(product.shipToRestrictedStates) })
                    .then(() => this.eventBus().emit('shipto.restrictions.updated.error.msg'));
            });
        }

        /**
         * @description Render Add to cart button
         * @param {object} product
         */
        renderAddToCartButton(product) {
            this.getById('addToCart', (addToCart) => {
                addToCart.render('template', {
                    pid: product.id,
                    childProducts: (product.productType === 'bundle' ? JSON.stringify(product.childProducts) : null),
                    shipToRestrictedStates: JSON.stringify(product.shipToRestrictedStates),
                    disableBtn: ((!product.available && product.availability
                            && product.availability.isOutOfStock) || product.isEmptyPrice),
                    inStock: (product.availability && product.availability.stock ? product.availability.stock : null),
                    isOutOfStock: (!product.available && product.availability && product.availability.isOutOfStock),
                    isPreorder: product.availability && product.availability.isPreorder
                })
                    .then(() => {
                        this.getById('product_disabledbtn', (disabledBtn) => {
                            disabledBtn.render('template', { pid: product.id })
                                .then(() => this.eventBus().emit('shipto.restrictions.updated.addtocart'));
                        });
                    });
            });
        }

        /**
         * @description widget triggered event
         * Saves `selectWidget.id` into a property for further global notifications etc.
         * Later, when triggering event, `selectWidget.id` will be analised in order to send with event correct data.
         * @param {inputSelect} selectWidget widget
         * @returns {void}
         */
        changeAttribute(selectWidget) {
            super.changeAttribute(selectWidget);
            const selectedOptions = selectWidget.getSelectedOptions();
            this.selectedVariationElement = selectedOptions;
        }

        /**
         * @description After change attrigute handler
         * @param {object} response response object
         * @returns {void}
         */
        afterChangeAttribute(response) {
            super.afterChangeAttribute(response);
            const gtmData = response.product.gtmInfo;
            if (this.selectedVariationElement.attr('name') !== 'quantity') {
                if (gtmData) {
                    gtmData.position = this.selectedVariationElement.data('productpreviousposition');
                    this.eventBus().emit('product.variation.click', gtmData, this.selectedVariationElement);
                }
            }

            this.eventBus().emit('update.wishlist.state', response.product.itemAlreadyExistsInWishList);
        }

        /**
         * @description Render product availability
         * @param {object} product Product object
         * @returns {void}
         */
        renderAvailability(product) {
            super.renderAvailability(product);

            let alert = '';

            if (product.availability && product.availability.alerts) {
                alert = product.availability.alerts.join('');
            }

            this.getById('availabilityAlert', (availabilityAlert) => {
                availabilityAlert.render('template', {
                    alert: alert
                }, availabilityAlert.ref('container'));
            });
        }

        /**
         * @description Sets preference to the data-attribute properties and widget properties
         * to store widget preference state.
         * @param {string} key - preference key
         * @param {any} value - preference value to set
         */
        setPref(key, value) {
            this.ref('self').data(key, value);

            this.config[key] = value;
        }

        /**
         * @description Handle back in stock notification
         * @param {object} product - product
         * @returns {void}
         */
        handleBackInStockNotification(product) {
            if (!product.enableBackInStockNotification) {
                this.ref('inStockActions').show();
                this.ref('outOfStockActions').hide();

                return;
            }

            if (product.availability && product.availability.isOutOfStock) {
                this.ref('inStockActions').hide();
                this.ref('outOfStockActions').show();
            } else {
                this.ref('inStockActions').show();
                this.ref('outOfStockActions').hide();
            }
        }

        /**
         * @description Update Add To Cart CTA for member products
         * @param {object} product - product
         * @returns {void}
         */
        updateAddToCartButton(product) {
            if (product.memberOnly) {
                this.ref('nonMemberProductActions').hide();
                this.ref('memberProductActions').show();
            } else {
                this.ref('memberProductActions').hide();
                this.ref('nonMemberProductActions').show();
            }
        }
    }

    return HarmonyProductDetail;
}
